// Cykliczność zadań — czysty moduł (bez DOM, bez strefy czasowej) wspólny dla backendu i frontendu.
// Spec: spec/zadania.md → "Zadania cykliczne". Testy: tests/unit/recurrence_core_test.ts.

export type RecurrenceUnit = 'day' | 'week' | 'month' | 'year';

export interface RecurrenceInterval {
  n: number;
  unit: RecurrenceUnit;
}

export const RECURRENCE_UNITS: RecurrenceUnit[] = ['day', 'week', 'month', 'year'];
export const RECURRENCE_MAX_N = 999;

/** Presety = skróty interwałów; są też postacią kanoniczną zapisu (formatRecurrenceRule). */
const PRESETS: Record<string, RecurrenceInterval> = {
  daily: { n: 1, unit: 'day' },
  weekly: { n: 1, unit: 'week' },
  biweekly: { n: 2, unit: 'week' },
  monthly: { n: 1, unit: 'month' },
  yearly: { n: 1, unit: 'year' },
};

/** Preset albo `every:N:UNIT` → interwał; null dla wartości pustej lub niepoprawnej. */
export function parseRecurrenceRule(rule: string | null | undefined): RecurrenceInterval | null {
  if (!rule) return null;
  const preset = PRESETS[rule];
  if (preset) return { ...preset };
  const m = /^every:(\d{1,3}):(day|week|month|year)$/.exec(rule);
  if (!m) return null;
  const n = Number(m[1]);
  if (n < 1 || n > RECURRENCE_MAX_N) return null;
  return { n, unit: m[2] as RecurrenceUnit };
}

/** Postać kanoniczna: preset, gdy istnieje (every:1:week → weekly), inaczej `every:N:UNIT`. */
export function formatRecurrenceRule(n: number, unit: RecurrenceUnit): string {
  for (const [name, p] of Object.entries(PRESETS)) {
    if (p.n === n && p.unit === unit) return name;
  }
  return `every:${n}:${unit}`;
}

/** Reguła w postaci kanonicznej albo null, gdy niepoprawna. */
export function normalizeRecurrenceRule(rule: string | null | undefined): string | null {
  const p = parseRecurrenceRule(rule);
  return p ? formatRecurrenceRule(p.n, p.unit) : null;
}

export function isValidRecurrenceRule(rule: string | null | undefined): boolean {
  return parseRecurrenceRule(rule) !== null;
}

/** Polska odmiana lat: 1 rok, 2–4 lata, 5–21 lat, 22–24 lata, 25–31 lat… */
function yearsWord(n: number): string {
  const last = n % 10;
  const last2 = n % 100;
  if (last >= 2 && last <= 4 && !(last2 >= 12 && last2 <= 14)) return 'lata';
  return 'lat';
}

/** Etykieta po polsku: „codziennie”, „co tydzień”, „co 3 tyg.”, „co 2 mies.”, „co 5 lat”. Nieznana reguła → surowa. */
export function recurrenceLabel(rule: string | null | undefined): string {
  const p = parseRecurrenceRule(rule);
  if (!p) return rule ?? '';
  switch (p.unit) {
    case 'day': return p.n === 1 ? 'codziennie' : `co ${p.n} dni`;
    case 'week': return p.n === 1 ? 'co tydzień' : `co ${p.n} tyg.`;
    case 'month': return p.n === 1 ? 'co miesiąc' : `co ${p.n} mies.`;
    case 'year': return p.n === 1 ? 'co rok' : `co ${p.n} ${yearsWord(p.n)}`;
  }
}

function pad2(n: number): string {
  return n < 10 ? '0' + n : String(n);
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();   // dzień 0 następnego miesiąca = ostatni dzień tego
}

/**
 * Następne wystąpienie: `dateISO` (YYYY-MM-DD) + interwał reguły. Arytmetyka na Y-M-D (UTC, bez strefy).
 * Miesiące/lata: ten sam dzień, a gdy go nie ma w miesiącu docelowym — jego ostatni dzień.
 * null dla niepoprawnej reguły lub daty.
 */
export function nextOccurrence(dateISO: string | null | undefined, rule: string | null | undefined): string | null {
  const p = parseRecurrenceRule(rule);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateISO ?? '');
  if (!p || !m) return null;
  let year = Number(m[1]);
  let month = Number(m[2]);
  let day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;

  if (p.unit === 'day' || p.unit === 'week') {
    const days = p.unit === 'day' ? p.n : p.n * 7;
    const d = new Date(Date.UTC(year, month - 1, day + days));
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  }

  const months = p.unit === 'month' ? p.n : p.n * 12;
  const total = (month - 1) + months;
  year += Math.floor(total / 12);
  month = (total % 12) + 1;
  day = Math.min(day, daysInMonth(year, month));
  return `${year}-${pad2(month)}-${pad2(day)}`;
}
